import { Button } from "components/ui/Button";
import { StatusBadge } from "components/ui/StatusBadge/StatusBadge";
import {
  type CreateCompanyContactInput,
  useCompanyMutations,
} from "hooks/useCompaniesApi";
import { useCompanyGroupsApi } from "hooks/useCompanyGroupsApi";
import { Plus, Trash2 } from "lucide-react";
import { type FC, useState } from "react";
import { companyGroupDotClass } from "utils/company-group-colors";
import { toastError } from "utils/toast-utils";

type ContactRow = {
  id: string;
  /** 主ドメイン入力時はローカル部、未入力時はフルメールアドレス */
  emailInput: string;
  displayName: string;
  optedOut: boolean;
};

type Props = {
  /** 作成成功時のコールバック (一覧の再取得・フォーム閉じ用) */
  onCreated: () => void;
  /** キャンセル時のコールバック */
  onCancel: () => void;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// 連絡先行の React key 用の一意な連番 (値の意味は持たない)
let rowSeq = 0;

/**
 * 空の連絡先行を生成する。
 * @return {ContactRow} 初期状態の連絡先行
 */
function emptyRow(): ContactRow {
  rowSeq += 1;
  return {
    id: `contact-row-${rowSeq}`,
    emailInput: "",
    displayName: "",
    optedOut: false,
  };
}

/**
 * 入力値と主ドメインから完全なメールアドレスを組み立てる。
 *
 * 主ドメインがある場合はローカル部のみの入力に主ドメインを補完する。
 * 入力値に既に "@" が含まれる場合はフルメールとしてそのまま採用する。
 * @param {string} raw 入力値 (ローカル部 または フルメール)
 * @param {string} primaryDomain 主ドメイン (未入力可)
 * @return {string} 正規化した完全メールアドレス (組み立て不能なら入力値を lowercase)
 */
function buildEmail(raw: string, primaryDomain: string): string {
  const value = raw.trim().toLowerCase();
  if (!value) {
    return "";
  }
  if (value.includes("@")) {
    return value;
  }
  const domain = primaryDomain.trim().toLowerCase();
  return domain ? `${value}@${domain}` : value;
}

/**
 * 新規企業作成フォーム (ISSUE-820 / ISSUE-821)。
 *
 * 企業の基本情報に加え、連絡先 (複数・可変) と企業グループ加入 (複数) を
 * 1 画面でまとめて登録する。連絡先のメールドメインは主ドメインに連動する。
 * @param {Props} props onCreated / onCancel コールバック
 * @return {JSX.Element} 新規企業作成フォーム
 */
export const CompanyCreateForm: FC<Props> = ({ onCreated, onCancel }) => {
  const { createCompany, loading } = useCompanyMutations();
  const { data: groupData } = useCompanyGroupsApi();

  const [name, setName] = useState("");
  const [primaryDomain, setPrimaryDomain] = useState("");
  const [description, setDescription] = useState("");
  const [contacts, setContacts] = useState<ContactRow[]>([emptyRow()]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(
    new Set(),
  );
  const [formError, setFormError] = useState<string | null>(null);

  const hasPrimaryDomain = primaryDomain.trim().length > 0;
  const groups = groupData?.items ?? [];

  const updateContact = (id: string, patch: Partial<ContactRow>) => {
    setContacts(rows => rows.map(r => (r.id === id ? { ...r, ...patch } : r)));
  };
  const addRow = () => setContacts(rows => [...rows, emptyRow()]);
  const removeRow = (id: string) =>
    setContacts(rows =>
      rows.length <= 1 ? rows : rows.filter(r => r.id !== id),
    );

  const toggleGroup = (id: string) => {
    setSelectedGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    setFormError(null);
    const trimmedName = name.trim();
    if (!trimmedName) {
      setFormError("企業名は必須です");
      return;
    }

    // 入力済みの連絡先行のみ email を組み立て、形式・フォーム内重複を検証する
    const builtContacts: CreateCompanyContactInput[] = [];
    const seen = new Set<string>();
    for (const row of contacts) {
      if (!row.emailInput.trim()) {
        continue;
      }
      const email = buildEmail(row.emailInput, primaryDomain);
      if (!EMAIL_RE.test(email)) {
        setFormError(
          `メールアドレスの形式が正しくありません: ${email || row.emailInput}`,
        );
        return;
      }
      if (seen.has(email)) {
        setFormError(`メールアドレスが重複しています: ${email}`);
        return;
      }
      seen.add(email);
      builtContacts.push({
        email,
        displayName: row.displayName.trim() || null,
        optedOut: row.optedOut,
      });
    }

    try {
      await createCompany({
        name: trimmedName,
        description: description.trim() || null,
        primaryDomain: primaryDomain.trim() || null,
        contacts: builtContacts,
        groupIds: Array.from(selectedGroupIds),
      });
      onCreated();
    } catch (e) {
      toastError(
        `作成に失敗しました: ${e instanceof Error ? e.message : "不明なエラー"}`,
      );
    }
  };

  return (
    <div className="mb-4 rounded-lg border border-slate-200 bg-white p-4 space-y-5">
      <h3 className="text-sm font-semibold">新規企業作成</h3>

      {formError && (
        <div className="alert alert-error text-sm py-2">
          <span>{formError}</span>
        </div>
      )}

      {/* 基本情報 */}
      <div>
        <div className="text-xs font-semibold text-base-content/50 mb-2">
          基本情報
        </div>
        <div className="grid gap-2 md:grid-cols-3">
          <input
            type="text"
            className="input input-bordered input-sm"
            placeholder="企業名 (必須)"
            value={name}
            onChange={e => setName(e.target.value)}
          />
          <input
            type="text"
            className="input input-bordered input-sm"
            placeholder="主ドメイン (任意, 例: acme.co.jp)"
            value={primaryDomain}
            onChange={e => setPrimaryDomain(e.target.value)}
          />
          <input
            type="text"
            className="input input-bordered input-sm"
            placeholder="メモ (任意)"
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        </div>
      </div>

      {/* 連絡先 (可変・複数) */}
      <div>
        <div className="text-xs font-semibold text-base-content/50 mb-2">
          連絡先（任意・複数可）
        </div>
        <div className="space-y-2">
          {contacts.map(row => (
            <div key={row.id} className="flex gap-2 items-center flex-wrap">
              <div className="flex items-center">
                <input
                  type="text"
                  className="input input-bordered input-sm w-56"
                  placeholder={
                    hasPrimaryDomain ? "メール (例: yamada)" : "メールアドレス"
                  }
                  value={row.emailInput}
                  onChange={e =>
                    updateContact(row.id, { emailInput: e.target.value })
                  }
                />
                {hasPrimaryDomain && !row.emailInput.includes("@") && (
                  <span className="ml-1 text-sm text-base-content/60 whitespace-nowrap">
                    @{primaryDomain.trim()}
                  </span>
                )}
              </div>
              <input
                type="text"
                className="input input-bordered input-sm w-40"
                placeholder="表示名 (任意)"
                value={row.displayName}
                onChange={e =>
                  updateContact(row.id, { displayName: e.target.value })
                }
              />
              <label className="flex items-center gap-1 text-xs text-base-content/70 whitespace-nowrap">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={row.optedOut}
                  onChange={e =>
                    updateContact(row.id, { optedOut: e.target.checked })
                  }
                />
                配信停止
              </label>
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={() => removeRow(row.id)}
                disabled={contacts.length <= 1}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
        <div className="mt-2">
          <Button type="button" variant="outline" size="xs" onClick={addRow}>
            <Plus className="w-3.5 h-3.5" />
            連絡先を追加
          </Button>
        </div>
      </div>

      {/* 企業グループ (複数選択) */}
      <div>
        <div className="text-xs font-semibold text-base-content/50 mb-2">
          企業グループに加入（任意・複数可）
        </div>
        {groups.length === 0 ? (
          <p className="text-sm text-base-content/60">
            登録済みの企業グループがありません
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {groups.map(g => (
              <label
                key={g.id}
                className="flex items-center gap-2 text-sm cursor-pointer"
              >
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={selectedGroupIds.has(g.id)}
                  onChange={() => toggleGroup(g.id)}
                />
                <span
                  className={`inline-block w-2.5 h-2.5 rounded-full ${companyGroupDotClass(
                    g.color,
                  )}`}
                  aria-hidden
                />
                {g.isBlacklisted && (
                  <StatusBadge size="xs" tone="tag-danger">
                    ブラックリスト
                  </StatusBadge>
                )}
                {/* 素のテキストノードにせず <span> で包む。flex アイテムとしての
                    描画は同じだが、テキストノードのままだと getByText("除外G") が
                    <label> 自体（= バッジを内包する要素）に一致してしまい、
                    DOM 順の検証ができない。 */}
                <span>{g.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-2 justify-end pt-2 border-t border-slate-100">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          キャンセル
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleSubmit}
          disabled={loading || !name.trim()}
        >
          作成
        </Button>
      </div>
    </div>
  );
};

export default CompanyCreateForm;
